// client/src/components/layout/TabStrip.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Plus, Pin } from 'lucide-react';
import { useTabs } from '../../context/TabContext';
import './TabStrip.css';

const NEW_TAB_PATH = '/';

export default function TabStrip() {
  const {
    tabs,
    activeId,
    activateTab,
    closeTab,
    openTab,
    reorderTab,
    closeOtherTabs,
    closeTabsToRight,
    duplicateTab,
    togglePin,
  } = useTabs();
  const [dragId, setDragId] = useState(null);
  const [menu, setMenu] = useState(null); // { tabId, x, y }

  const handleDragStart = useCallback((event, tabId) => {
    setDragId(tabId);
    event.dataTransfer.effectAllowed = 'move';
    // Firefox needs data set for a drag to start at all.
    event.dataTransfer.setData('text/plain', tabId);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (event, index) => {
      event.preventDefault();
      if (dragId) reorderTab(dragId, index);
      setDragId(null);
    },
    [dragId, reorderTab]
  );

  // A single tab is just the page — the strip would be chrome with nothing to do.
  if (tabs.length < 2) return <SingleTabStrip onNew={() => openTab(NEW_TAB_PATH)} />;

  return (
    <div className="tab-strip" role="tablist" aria-label="Open tabs">
      <div className="tab-strip-scroll">
        {tabs.map((tab, index) => (
          <div
            key={tab.id}
            role="tab"
            tabIndex={0}
            aria-selected={tab.id === activeId}
            title={tab.label}
            draggable
            onDragStart={(e) => handleDragStart(e, tab.id)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={() => setDragId(null)}
            className={[
              'tab-item',
              tab.id === activeId ? 'tab-item--active' : '',
              dragId === tab.id ? 'tab-item--dragging' : '',
              tab.discarded ? 'tab-item--discarded' : '',
              tab.pinned ? 'tab-item--pinned' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => activateTab(tab.id)}
            onContextMenu={(e) => {
              e.preventDefault();
              setMenu({ tabId: tab.id, x: e.clientX, y: e.clientY });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                activateTab(tab.id);
              }
            }}
            // Middle-clicking a tab closes it, same as a browser tab strip.
            onAuxClick={(e) => {
              if (e.button === 1) {
                e.preventDefault();
                closeTab(tab.id);
              }
            }}
          >
            {tab.pinned && <Pin size={11} className="tab-item-pin" aria-hidden="true" />}
            <span className="tab-item-label">{tab.label}</span>
            {/* A pinned tab is meant to stay put, so it loses the quick close. */}
            {!tab.pinned && (
              <button
                type="button"
                className="tab-item-close"
                aria-label={`Close ${tab.label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  closeTab(tab.id);
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        className="tab-strip-new"
        aria-label="New tab"
        title="New tab (Ctrl+T)"
        onClick={() => openTab(NEW_TAB_PATH)}
      >
        <Plus size={15} />
      </button>

      {menu && (
        <TabContextMenu
          x={menu.x}
          y={menu.y}
          tab={tabs.find((t) => t.id === menu.tabId)}
          onClose={() => setMenu(null)}
          actions={{ duplicateTab, togglePin, closeTab, closeOtherTabs, closeTabsToRight }}
          canClose={tabs.length > 1}
        />
      )}
    </div>
  );
}

function TabContextMenu({ x, y, tab, onClose, actions, canClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const dismiss = (e) => {
      if (!ref.current || !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    // Deferred: the click that opened the menu is still propagating.
    const id = setTimeout(() => {
      document.addEventListener('mousedown', dismiss);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!tab) return null;

  const run = (fn) => () => {
    fn(tab.id);
    onClose();
  };
  const items = [
    { label: 'Duplicate', onClick: run(actions.duplicateTab) },
    { label: tab.pinned ? 'Unpin' : 'Pin', onClick: run(actions.togglePin) },
    { label: 'Close others', onClick: run(actions.closeOtherTabs), disabled: !canClose },
    { label: 'Close to the right', onClick: run(actions.closeTabsToRight), disabled: !canClose },
    { label: 'Close', onClick: run(actions.closeTab), disabled: !canClose || tab.pinned },
  ];

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className="tab-menu"
      role="menu"
      // Nudged inboard so a right-click near the window edge stays on screen.
      style={{ left: Math.min(x, window.innerWidth - 190), top: y }}
    >
      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          role="menuitem"
          className="tab-menu-item"
          disabled={item.disabled}
          onClick={item.onClick}
        >
          {item.label}
        </button>
      ))}
    </div>,
    document.body
  );
}

/**
 * With one tab open there is nothing to switch between, so the strip collapses to
 * just the affordance that creates a second one. Keeps the chrome quiet until
 * tabs are actually in use.
 */
function SingleTabStrip({ onNew }) {
  return (
    <div className="tab-strip tab-strip--single">
      <button
        type="button"
        className="tab-strip-new"
        aria-label="New tab"
        title="New tab (Ctrl+T)"
        onClick={onNew}
      >
        <Plus size={15} />
      </button>
    </div>
  );
}
